import React, { useState } from "react";
import axios from "axios";
import { Send, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL =
    process.env.REACT_APP_BACKEND_URL || import.meta?.env?.REACT_APP_BACKEND_URL || "";

const TOPIC_OPTIONS = [
    { value: "account", label: "Account & sign-in" },
    { value: "privacy", label: "Privacy & data" },
    { value: "billing", label: "Billing & subscriptions" },
    { value: "feature", label: "Using the app" },
    { value: "bug", label: "Bugs & feedback" },
    { value: "legal", label: "Legal & policies" },
    { value: "other", label: "Something else" },
];

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        topic: "feature",
        message: "",
    });
    const [touched, setTouched] = useState({});
    const [status, setStatus] = useState("idle"); // idle|sending|sent|error
    const [errorMsg, setErrorMsg] = useState("");
    const [reference, setReference] = useState("");

    const errors = {
        name: form.name.trim().length < 1 ? "Please share your name." : "",
        email: !emailOk(form.email) ? "Enter a valid email address." : "",
        message:
            form.message.trim().length < 10
                ? "A sentence or two helps us help you faster."
                : "",
    };
    const isValid = !errors.name && !errors.email && !errors.message;

    const onChange = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));
    const onBlur = (field) => () =>
        setTouched((t) => ({ ...t, [field]: true }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setTouched({ name: true, email: true, message: true });
        if (!isValid || status === "sending") return;
        setStatus("sending");
        setErrorMsg("");
        try {
            const res = await axios.post(
                `${BACKEND_URL}/api/support/contact`,
                {
                    name: form.name.trim(),
                    email: form.email.trim(),
                    topic: form.topic,
                    message: form.message.trim(),
                },
                { timeout: 15000 },
            );
            setReference(res?.data?.reference || "");
            setStatus("sent");
        } catch (err) {
            const detail =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                err?.message ||
                "Something went wrong.";
            setErrorMsg(
                typeof detail === "string"
                    ? detail
                    : "Something went wrong. Please try again.",
            );
            setStatus("error");
        }
    };

    if (status === "sent") {
        return (
            <div
                data-testid="support-form-success"
                className="rounded-[24px] bg-white border border-brand p-7 sm:p-9 shadow-[var(--shadow-soft)]"
                role="status"
                aria-live="polite"
            >
                <span className="inline-flex items-center justify-center h-12 w-12 rounded-[14px] bg-[rgba(48,140,138,0.12)] border border-[rgba(48,140,138,0.22)] text-[var(--brand-teal-2)]">
                    <CheckCircle2 size={22} strokeWidth={1.8} />
                </span>
                <h3 className="mt-5 font-display text-[26px] sm:text-[30px] tracking-[-0.018em] leading-[1.1] text-[var(--brand-ink)]">
                    Thanks — your message is with us.
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[var(--brand-muted-2)] max-w-[58ch]">
                    We read every note that comes through. You&rsquo;ll hear
                    back from a real person at{" "}
                    <strong className="text-[var(--brand-ink)]">
                        {form.email}
                    </strong>
                    , usually within two business days.
                </p>
                {reference ? (
                    <p
                        data-testid="support-form-reference"
                        className="mt-5 inline-flex items-center gap-2 rounded-full border border-brand bg-[var(--brand-paper-2)] px-3.5 py-1.5 text-[12px] font-medium text-[var(--brand-muted-2)]"
                    >
                        <span className="uppercase tracking-[0.14em] text-[var(--brand-teal-2)]">
                            Reference
                        </span>
                        <span className="font-mono tracking-[0.05em] text-[var(--brand-ink)]">
                            {reference}
                        </span>
                    </p>
                ) : null}
                <button
                    type="button"
                    onClick={() => {
                        setForm({
                            name: "",
                            email: "",
                            topic: "feature",
                            message: "",
                        });
                        setTouched({});
                        setStatus("idle");
                        setReference("");
                    }}
                    data-testid="support-form-new"
                    className="mt-6 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[var(--brand-navy)] hover:text-[var(--brand-teal-2)] transition-colors"
                >
                    Send another message →
                </button>
            </div>
        );
    }

    return (
        <form
            data-testid="support-contact-form"
            onSubmit={onSubmit}
            noValidate
            className="rounded-[24px] bg-white border border-brand p-7 sm:p-9 shadow-[var(--shadow-soft)]"
        >
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--brand-teal-2)]">
                        Send us a note
                    </p>
                    <h2 className="mt-2 font-display text-[26px] sm:text-[30px] leading-[1.1] tracking-[-0.018em] text-[var(--brand-ink)]">
                        We&rsquo;ll get back to you personally.
                    </h2>
                </div>
            </div>

            <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <Field
                    label="Your name"
                    htmlFor="sf-name"
                    error={touched.name && errors.name}
                >
                    <input
                        id="sf-name"
                        type="text"
                        autoComplete="name"
                        value={form.name}
                        onChange={onChange("name")}
                        onBlur={onBlur("name")}
                        data-testid="support-form-name"
                        placeholder="What should we call you?"
                        className={inputClass(touched.name && errors.name)}
                        maxLength={120}
                    />
                </Field>

                <Field
                    label="Your email"
                    htmlFor="sf-email"
                    error={touched.email && errors.email}
                >
                    <input
                        id="sf-email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={onChange("email")}
                        onBlur={onBlur("email")}
                        data-testid="support-form-email"
                        placeholder="you@example.com"
                        className={inputClass(touched.email && errors.email)}
                        maxLength={200}
                    />
                </Field>
            </div>

            <div className="mt-5">
                <Field label="Topic" htmlFor="sf-topic">
                    <div className="relative">
                        <select
                            id="sf-topic"
                            value={form.topic}
                            onChange={onChange("topic")}
                            data-testid="support-form-topic"
                            className={`${inputClass(false)} appearance-none pr-10`}
                        >
                            {TOPIC_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                        <span
                            aria-hidden
                            className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--brand-muted-2)]"
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    d="M5 8l5 5 5-5"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </div>
                </Field>
            </div>

            <div className="mt-5">
                <Field
                    label="Message"
                    htmlFor="sf-message"
                    error={touched.message && errors.message}
                    hint="Include your device/OS and BetweenUs version if it's a bug."
                >
                    <textarea
                        id="sf-message"
                        rows={6}
                        value={form.message}
                        onChange={onChange("message")}
                        onBlur={onBlur("message")}
                        data-testid="support-form-message"
                        placeholder="Tell us what's going on…"
                        className={`${inputClass(
                            touched.message && errors.message,
                        )} resize-y min-h-[140px]`}
                        maxLength={5000}
                    />
                </Field>
            </div>

            {status === "error" ? (
                <div
                    data-testid="support-form-error"
                    role="alert"
                    className="mt-5 flex items-start gap-2.5 rounded-[12px] bg-[rgba(183,54,43,0.06)] border border-[rgba(183,54,43,0.18)] text-[var(--brand-ink)] px-4 py-3"
                >
                    <AlertCircle
                        size={16}
                        className="mt-0.5 flex-none text-[#B7362B]"
                    />
                    <p className="text-[13.5px] leading-snug">
                        {errorMsg}{" "}
                        <span className="text-[var(--brand-muted-2)]">
                            You can also email{" "}
                            <a
                                href="mailto:support@tylegn.com"
                                className="underline decoration-[rgba(26,58,108,0.3)] hover:decoration-[var(--brand-teal-2)] underline-offset-[3px] font-medium"
                            >
                                support@tylegn.com
                            </a>
                            .
                        </span>
                    </p>
                </div>
            ) : null}

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-[12px] text-[var(--brand-muted)] max-w-[52ch]">
                    By submitting, you agree to our{" "}
                    <a
                        href="/privacy"
                        className="underline decoration-[rgba(26,58,108,0.3)] hover:decoration-[var(--brand-teal-2)] underline-offset-[3px] text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)]"
                    >
                        Privacy Policy
                    </a>
                    . We use your message only to reply to you.
                </p>
                <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="support-form-submit"
                    className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[14px] font-semibold tracking-[-0.01em] shadow-[0_10px_24px_rgba(14,27,42,0.14)] hover:bg-[var(--brand-navy-2)] hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98] transition-[background-color,transform,box-shadow] duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                    {status === "sending" ? (
                        <>
                            <Loader2
                                size={16}
                                className="animate-spin"
                                strokeWidth={2.2}
                            />
                            Sending…
                        </>
                    ) : (
                        <>
                            <Send size={15} strokeWidth={2} />
                            Send message
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}

function Field({ label, htmlFor, error, hint, children }) {
    return (
        <label htmlFor={htmlFor} className="block">
            <span className="block text-[12.5px] font-semibold tracking-[-0.01em] text-[var(--brand-ink)]">
                {label}
            </span>
            <div className="mt-1.5">{children}</div>
            {error ? (
                <span
                    className="mt-1.5 block text-[12px] text-[#B7362B]"
                    role="alert"
                >
                    {error}
                </span>
            ) : hint ? (
                <span className="mt-1.5 block text-[12px] text-[var(--brand-muted)]">
                    {hint}
                </span>
            ) : null}
        </label>
    );
}

function inputClass(hasError) {
    return [
        "w-full rounded-[12px] bg-white border px-3.5 py-3 text-[14.5px] leading-snug text-[var(--brand-ink)] placeholder:text-[var(--brand-muted)] transition-[border-color,box-shadow] duration-150",
        "focus:outline-none focus:ring-2 focus:ring-[rgba(48,140,138,0.35)] focus:ring-offset-1 focus:ring-offset-white",
        hasError
            ? "border-[rgba(183,54,43,0.45)] focus:ring-[rgba(183,54,43,0.25)]"
            : "border-[var(--brand-border)] hover:border-[var(--brand-border-strong)]",
    ].join(" ");
}
